import { useMemo } from 'react';
import { usePredictionMarketCategories } from '@/lib/endpoints';
import { TRENDING_CATEGORY_CODE, useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useHiddenCategories } from '@/lib/territory/useTerritory';
import { useCloseSearchOverlay } from '@/store';
import { Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { CategoryResponseDto } from '@/lib/models';

/** Synthetic leading pill that clears the category filter ("all" view). */
const TRENDING_PILL: CategoryResponseDto = {
  id: 'trending',
  code: TRENDING_CATEGORY_CODE,
  displayName: 'Trending',
  iconImgUrl: null,
  subCategories: [],
};

/**
 * Wrapping category-filter pills for the search page (ARC-113 follow-up). Same
 * category source and `?category=` URL contract as the home tabs
 * ({@link useAppSearchParams}), but laid out wrapping (per design) rather than
 * in a horizontal scroller, and without the sub-category row. Selecting a pill
 * behaves like the home category tabs: it navigates the feed to that
 * `categoryCode` ("Trending" clears it) and closes the search overlay, so the
 * player lands on the category page rather than filtering search in place.
 */
export function SearchCategoryFilter({ className }: { className?: string }) {
  const { setCategory } = useAppSearchParams();
  const { data, isLoading } = usePredictionMarketCategories();
  const hiddenCategories = useHiddenCategories();
  const closeSearch = useCloseSearchOverlay();

  const pills = useMemo<CategoryResponseDto[]>(() => {
    const visible = (data ?? []).filter((cat) => !hiddenCategories.includes(cat.code.toUpperCase()));
    return [TRENDING_PILL, ...visible];
  }, [data, hiddenCategories]);

  if (isLoading) {
    return (
      <div className={cn('flex flex-wrap gap-2 px-3.5 md:px-0', className)}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-24 rounded-full" />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) return null;

  return (
    <div
      className={cn('search-stagger-up flex flex-wrap gap-2 px-3.5 md:px-0', className)}
      role="group"
      aria-label="Filter by category"
    >
      {pills.map((cat) => (
        // Pills act as navigation shortcuts only — they never reflect the active
        // category, so they always render in the unselected (neutral) style.
        <button
          key={cat.code}
          type="button"
          onClick={() => {
            setCategory(cat.code);
            closeSearch();
          }}
          data-testid={`search-filter-${cat.code.toLowerCase()}`}
          className={cn(
            'flex items-center justify-center h-9 px-3.5 rounded-full border text-[16px] capitalize whitespace-nowrap cursor-pointer transition-colors',
            'bg-surface text-ink border-stroke-soft font-normal',
          )}
        >
          <span>{cat.displayName || cat.code}</span>
        </button>
      ))}
    </div>
  );
}
